import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  BellRing,
  Braces,
  CalendarClock,
  Check,
  ChevronDown,
  Clock,
  Database,
  Gauge,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  Table2,
  X,
} from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { DetectionDemo } from '@/components/marketing/DetectionDemo';
import {
  PERIODE_PER,
  hargaRingkas,
  usePublicPlans,
  useSiteContent,
} from '@/components/marketing/usePublic';
import { cn } from '@/lib/cn';

/**
 * Halaman publik.
 *
 * Dirancang ulang dengan tiga keputusan yang membedakannya dari halaman produk
 * data pada umumnya:
 *
 * 1. **Gelap dan terang berselang.** Bagian gelap menahan perhatian, bagian
 *    terang memberi ruang bernapas. Satu latar putih dari atas sampai bawah
 *    membuat semua bagian terasa sama pentingnya - yang berarti tidak ada yang
 *    terasa penting.
 * 2. **Aksen lime, bukan biru korporat.** Justru karena tidak aman. Biru dan
 *    gradasi ungu adalah dua hal paling sering terlihat di kategori ini.
 * 3. **Demo yang berjalan sendiri, bukan tangkapan layar.** Janji "tempel URL,
 *    kolomnya terisi sendiri" sulit dipercaya kalau hanya dibaca.
 */
export const LandingPage = () => {
  const { konten, dikelola } = useSiteContent();
  const [tinggiPengumuman, setTinggiPengumuman] = useState(0);

  return (
    <div className="min-h-screen bg-[var(--color-void-950)] text-white">
      {/* Pengumuman dan navigasi berbagi satu blok yang menempel di puncak.
          Dulu bannernya berada di aliran biasa sementara navbarnya `fixed`,
          jadi navbar menutupinya sepenuhnya dan pengumumannya tidak pernah
          terbaca siapa pun. Menjadikan keduanya satu blok membuat navbar
          selalu berada DI BAWAH banner, berapa pun tingginya. */}
      <div className="fixed inset-x-0 top-0 z-50">
        <Pengumuman isi={konten?.announcement} onUkur={setTinggiPengumuman} />
        <Nav />
      </div>

      {/* Hero didorong turun setinggi banner, dihitung dari elemennya
          sendiri. Angka tetap akan salah begitu pengumumannya dua baris atau
          memuat gambar. */}
      <div style={{ paddingTop: tinggiPengumuman }}>
        <Hero konten={dikelola ? konten : null} />
      </div>
      <Bukti stats={dikelola && konten?.stats?.length ? konten.stats : null} />
      <Masalah />
      <Fitur />
      <CaraKerja />
      <Harga />
      <Kepercayaan faq={konten?.faq?.length ? konten.faq : null} />
      <Tentang isi={konten?.tentang} />
      <Kontak isi={konten?.kontak} />
      <Penutup />
      <Footer kontak={konten?.contactEmail} konten={konten} />
    </div>
  );
};

/* ── Pengumuman ────────────────────────────────────────────────────────── */

const NADA_PENGUMUMAN = {
  promo: 'bg-signal-400 text-[var(--color-void-950)]',
  info: 'bg-sky-500 text-white',
  peringatan: 'bg-amber-400 text-[var(--color-void-950)]',
  gelap: 'bg-white/10 text-white backdrop-blur-xl border-b border-white/10',
};

const LEBAR_PENGUMUMAN = {
  sempit: 'max-w-2xl',
  sedang: 'max-w-4xl',
  penuh: 'max-w-7xl',
};

/**
 * Banner pengumuman di puncak halaman depan.
 *
 * Tingginya dilaporkan ke induk lewat `onUkur`, bukan ditebak dengan angka
 * tetap. Banner bisa satu baris, bisa dua baris beserta gambar dan tombol,
 * dan hero yang didorong turun sejauh angka tetap akan salah pada salah
 * satunya.
 *
 * Yang ditutup pembaca diingat di perambannya sendiri, ditandai isi
 * pengumumannya. Menyimpan sekadar "sudah ditutup" membuat pengumuman
 * BERIKUTNYA ikut tersembunyi, dan itu kegagalan yang tidak akan pernah
 * dilaporkan siapa pun karena tidak ada yang tahu ada yang hilang.
 */
const Pengumuman = ({ isi, onUkur }) => {
  const [ditutup, setDitutup] = useState(false);
  const [elemen, setElemen] = useState(null);

  const tampil = Boolean(isi?.enabled && (isi.text || isi.judul));
  const sidik = tampil ? `${isi.judul ?? ''}|${isi.text ?? ''}`.slice(0, 120) : '';

  useEffect(() => {
    if (!tampil) return;
    try {
      setDitutup(window.localStorage.getItem('crawel:pengumuman') === sidik);
    } catch {
      // Peramban yang melarang penyimpanan lokal tetap boleh melihat
      // pengumumannya. Tidak bisa mengingat penutupan jauh lebih ringan
      // akibatnya daripada halaman yang gagal dimuat.
    }
  }, [tampil, sidik]);

  // Tinggi diukur ulang saat isinya berubah dan saat lebar layar berubah,
  // karena teks yang muat satu baris di laptop menjadi tiga baris di ponsel.
  useEffect(() => {
    if (!elemen) {
      onUkur?.(0);
      return undefined;
    }
    const ukur = () => onUkur?.(elemen.offsetHeight);
    ukur();

    const pengamat = new ResizeObserver(ukur);
    pengamat.observe(elemen);
    return () => pengamat.disconnect();
  }, [elemen, onUkur]);

  if (!tampil || ditutup) return null;

  const tutup = () => {
    setDitutup(true);
    try {
      window.localStorage.setItem('crawel:pengumuman', sidik);
    } catch {
      // Lihat catatan di atas.
    }
  };

  const adaTombol = Boolean(isi.tautanUrl && isi.tautanLabel);

  return (
    <div
      ref={setElemen}
      className={cn(
        'relative px-4 py-2.5',
        // Ruang di kanan disediakan untuk tombol tutup, supaya teks yang
        // panjang tidak berjalan ke bawahnya.
        isi.bisaDitutup !== false && 'pr-12',
        NADA_PENGUMUMAN[isi.nada] ?? NADA_PENGUMUMAN.promo,
      )}
    >
      <div
        className={cn(
          'mx-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-center',
          LEBAR_PENGUMUMAN[isi.lebar] ?? LEBAR_PENGUMUMAN.sedang,
        )}
      >
        {isi.gambarUrl && (
          <img
            src={isi.gambarUrl}
            alt=""
            // Kosong dan disembunyikan dari pembaca layar: gambarnya hiasan,
            // dan keterangan yang mengulang teks di sebelahnya justru
            // membuat pengumumannya terbaca dua kali.
            aria-hidden="true"
            className="h-6 w-auto shrink-0 object-contain"
            onError={(e) => {
              // Alamat gambar yang salah tidak boleh menyisakan ikon rusak
              // di tengah pengumuman.
              e.currentTarget.style.display = 'none';
            }}
          />
        )}

        <p className="text-sm leading-snug">
          {isi.judul && <span className="font-bold">{isi.judul}</span>}
          {isi.judul && isi.text && <span className="mx-1.5 opacity-50">|</span>}
          {isi.text && <span className="font-medium">{isi.text}</span>}
        </p>

        {adaTombol && (
          <a
            href={isi.tautanUrl}
            className="shrink-0 rounded-full bg-[var(--color-void-950)]/15 px-3 py-1 text-xs font-bold underline-offset-2 hover:underline"
          >
            {isi.tautanLabel}
          </a>
        )}
      </div>

      {isi.bisaDitutup !== false && (
        <button
          type="button"
          onClick={tutup}
          aria-label="Tutup pengumuman"
          className="absolute top-1.5 right-3 rounded-full p-1.5 opacity-60 transition-opacity hover:opacity-100"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

/* ── Navigasi ──────────────────────────────────────────────────────────── */

/**
 * Menu navigasi.
 *
 * Label pertama dulu berbunyi "Masalah". Di dalam navbar, kata itu terbaca
 * seperti situsnya sedang melaporkan gangguan, bukan seperti menu. Diganti
 * mengikuti judul bagian yang ditujunya sendiri, yaitu "Kenapa Crawel ada".
 */
const TAUTAN = [
  ['/#masalah', 'Kenapa Crawel'],
  ['/#cara-kerja', 'Cara Kerja'],
  ['/fitur', 'Fitur'],
  ['/harga', 'Harga'],
  ['/dokumentasi', 'Dokumentasi'],
];

/**
 * Tautan pada bilah navigasi.
 *
 * Sama dengan `TAUTAN` ditambah Hubungi Kami. Sengaja daftar terpisah: di
 * footer, `TAUTAN` mengisi kolom "Produk", dan kolom "Kontak" sudah berdiri
 * sendiri di sebelahnya. Menaruh Hubungi Kami di keduanya membuat footer
 * menyebut hal yang sama dua kali.
 *
 * Kenapa ia perlu ada di bilah atas: cara menghubungi yang hanya bisa
 * ditemukan dengan menggulir sampai dasar halaman sama saja dengan tidak ada
 * bagi orang yang sedang mencarinya, entah itu calon pembeli yang ragu atau
 * peninjau penyedia pembayaran yang sedang memeriksa apakah ada usaha nyata
 * di balik situs ini.
 */
const TAUTAN_NAV = [...TAUTAN, ['/hubungi-kami', 'Hubungi Kami']];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  /**
   * Menggulung halus ke bagian yang dituju kalau kita memang sedang di
   * halaman depan.
   *
   * Tanpa ini, menekan menu jangkar dari halaman depan memicu MUAT ULANG
   * PENUH, karena tautannya `<a href="/#...">` dan peramban memperlakukan
   * "/" sebagai alamat baru. Layar berkedip putih sesaat, dan itu terlihat
   * seperti situsnya bermasalah padahal cuma berpindah bagian.
   */
  const keBagian = (e, href) => {
    if (!href.startsWith('/#') || pathname !== '/') return;
    const target = document.getElementById(href.slice(2));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', href);
    setOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        // Posisi menempelnya diatur pembungkus di LandingPage, bukan di sini.
        // Dulu header ini `fixed top-0` sendiri, jadi ia tetap melekat di
        // puncak layar dan menimpa banner pengumuman berapa pun tingginya.
        'transition-colors duration-300',
        // Transparan di puncak halaman supaya hero terasa penuh; menjadi solid
        // begitu digulir agar teks di baliknya tetap terbaca.
        scrolled
          ? 'border-b border-white/8 bg-[var(--color-void-950)]/85 backdrop-blur-xl'
          : 'border-b border-transparent',
      )}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-6">
        <Logo variant="light" />

        <nav className="hidden items-center gap-7 md:flex" aria-label="Navigasi halaman">
          {TAUTAN_NAV.map(([href, label]) => (
            <a
              key={href}
              href={href}
              onClick={(e) => keBagian(e, href)}
              className="text-sm font-medium text-white/55 transition-colors hover:text-white"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/masuk"
            className="hidden px-3 py-2 text-sm font-semibold text-white/70 transition-colors hover:text-white sm:block"
          >
            Masuk
          </Link>
          <Link
            to="/daftar"
            className="rounded-lg bg-signal-400 px-4 py-2 text-sm font-bold text-void-950 transition-transform hover:scale-[1.03] active:scale-100"
          >
            Coba Gratis
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Tutup menu' : 'Buka menu'}
            aria-expanded={open}
            className="-mr-1 rounded-lg p-2 text-white/70 md:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          className="border-t border-white/8 bg-[var(--color-void-950)] px-5 py-3 md:hidden"
          aria-label="Navigasi halaman"
        >
          {TAUTAN_NAV.map(([href, label]) => (
            <a
              key={href}
              href={href}
              onClick={(e) => {
                keBagian(e, href);
                setOpen(false);
              }}
              className="block rounded-lg px-2 py-2.5 text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white"
            >
              {label}
            </a>
          ))}
          <Link
            to="/masuk"
            className="block rounded-lg px-2 py-2.5 text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white"
          >
            Masuk
          </Link>
        </nav>
      )}
    </header>
  );
};

/* ── Hero ──────────────────────────────────────────────────────────────── */

const Hero = ({ konten }) => (
  <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
    <div className="pointer-events-none absolute inset-0 grid-bg" aria-hidden="true" />
    <div
      className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,var(--color-signal-500),transparent_65%)] opacity-[0.10] blur-3xl"
      aria-hidden="true"
    />

    {/* `min-w-0` pada anak grid itu wajib di sini: tanpa itu, teks tak
        terputus di dalam demo (URL panjang) memaksa kolom melebihi lebar
        layar dan menyeret seluruh hero keluar viewport di ponsel. */}
    <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
      <div className="min-w-0">
        <a
          href="#fitur"
          className="inline-flex items-center gap-2 rounded-full border border-signal-400/25 bg-signal-400/8 px-3.5 py-1.5 text-xs font-bold text-signal-300 transition-colors hover:bg-signal-400/14"
        >
          <Sparkles className="size-3.5" aria-hidden="true" />
          {konten?.hero?.badge || 'Deteksi kolom otomatis'}
          <ArrowRight className="size-3" aria-hidden="true" />
        </a>

        {konten?.hero?.title ? (
          <h1 className="display mt-6 text-[2.75rem] font-extrabold sm:text-6xl lg:text-[4.25rem]">
            {konten.hero.title}
          </h1>
        ) : (
          <h1 className="display mt-6 text-[2.75rem] font-extrabold sm:text-6xl lg:text-[4.25rem]">
            Ambil data web
            <br />
            <span className="text-signal-400">tanpa menulis</span>
            <br />
            satu baris kode
          </h1>
        )}

        <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-white/55">
          {konten?.hero?.subtitle ||
            'Tempel alamat situsnya. Crawel menemukan sendiri daftar yang berulang, menebak nama kolomnya, dan menyusun datanya jadi tabel rapi yang siap diunduh.'}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link
            to="/daftar"
            className="group inline-flex items-center gap-2 rounded-xl bg-signal-400 px-6 py-3.5 text-[15px] font-bold text-void-950 transition-transform hover:scale-[1.03] active:scale-100"
          >
            Mulai Gratis
            <ArrowRight
              className="size-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
          <a
            href="#cara-kerja"
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-6 py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-white/5"
          >
            Lihat cara kerjanya
          </a>
        </div>

        <p className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] text-white/35">
          <span className="flex items-center gap-1.5">
            <Check className="size-3.5 text-signal-400" aria-hidden="true" />
            150 baris gratis tiap bulan
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="size-3.5 text-signal-400" aria-hidden="true" />
            Tanpa kartu kredit
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="size-3.5 text-signal-400" aria-hidden="true" />
            Crawl pertama selesai dalam hitungan menit
          </span>
        </p>
      </div>

      <div className="min-w-0">
        <DetectionDemo />
      </div>
    </div>
  </section>
);

/* ── Bukti angka ───────────────────────────────────────────────────────── */

/**
 * Angka yang tampil di bawah hero.
 *
 * Semuanya harus bisa Anda buktikan kalau ada yang bertanya. Angka "uptime
 * 99,8 persen" sempat ada di sini dan dibuang: tidak ada yang pernah
 * mengukurnya, dan janji yang tidak bisa dibuktikan merusak kepercayaan pada
 * angka lain yang sebenarnya benar.
 */
const ANGKA = [
  ['150rb', 'Baris data pada paket tertinggi'],
  ['2', 'Mesin crawl: cepat dan browser penuh'],
  ['4', 'Format ekspor: CSV, JSON, NDJSON, Excel'],
  ['0', 'Baris kode yang perlu Anda tulis'],
];

const Bukti = ({ stats }) => (
  <section className="border-y border-white/8 bg-[var(--color-void-1000)]">
    <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px overflow-hidden px-5 sm:px-6 lg:grid-cols-4">
      {(stats ? stats.map((s) => [s.value, s.label]) : ANGKA).map(([nilai, label]) => (
        <div key={label} className="px-2 py-9 lg:px-6">
          <p className="text-3xl font-extrabold tracking-tight text-white lg:text-4xl">{nilai}</p>
          <p className="mt-1.5 text-[13px] leading-snug text-white/40">{label}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ── Masalah → Solusi ──────────────────────────────────────────────────── */

const Masalah = () => (
  <section id="masalah" className="bg-[var(--color-ink-50)] py-20 text-brand-950 lg:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-6">
      <Eyebrow tone="dark">Kenapa Crawel ada</Eyebrow>
      <h2 className="display mt-4 max-w-2xl text-3xl font-extrabold sm:text-4xl lg:text-[2.75rem]">
        Bagian tersulit dari scraping bukan crawling-nya
      </h2>
      <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-ink-600">
        Yang sulit itu memberi tahu mesin bagian mana dari halaman yang harus
        diambil. Alat lain menyerahkan pekerjaan itu kepada Anda, lengkap
        dengan Inspect Element dan selektor yang harus disalin satu per satu.
      </p>

      <div className="mt-12 grid gap-5 lg:grid-cols-2">
        {/* Sebelum */}
        <div className="rounded-2xl border border-ink-200 bg-white p-7">
          <div className="flex items-center gap-2.5">
            <span className="grid size-8 place-items-center rounded-lg bg-danger-50 text-danger-600">
              <Braces className="size-4" aria-hidden="true" />
            </span>
            <p className="text-sm font-bold text-ink-500">Cara lama</p>
          </div>

          <p className="mt-5 text-[15px] leading-relaxed text-ink-600">
            Buka Inspect Element, cari elemen yang benar, salin selektornya,
            lalu ulangi untuk setiap kolom. Salah satu huruf saja, hasilnya
            kosong tanpa memberi tahu di mana salahnya.
          </p>

          <div className="mt-5 space-y-1.5 rounded-xl bg-ink-950 p-4 font-mono text-[12px] leading-relaxed">
            <p className="text-ink-400">// dan begini untuk tiap kolom…</p>
            <p className="text-ink-300">
              <span className="text-danger-400">div</span>.css-1x2y3z {'>'} .item-wrap
            </p>
            <p className="text-ink-300">
              article {'>'} h3 {'>'} <span className="text-danger-400">a</span>[title]
            </p>
            <p className="text-ink-300">
              span.price-<span className="text-danger-400">value</span>::text
            </p>
          </div>
        </div>

        {/* Sesudah */}
        <div className="relative overflow-hidden rounded-2xl border border-brand-950 bg-[var(--color-void-950)] p-7 text-white">
          <div
            className="pointer-events-none absolute -top-24 -right-24 size-64 rounded-full bg-[radial-gradient(circle,var(--color-signal-500),transparent_70%)] opacity-20 blur-2xl"
            aria-hidden="true"
          />

          <div className="relative flex items-center gap-2.5">
            <span className="grid size-8 place-items-center rounded-lg bg-signal-400/15 text-signal-400">
              <Sparkles className="size-4" aria-hidden="true" />
            </span>
            <p className="text-sm font-bold text-signal-400">Dengan Crawel</p>
          </div>

          <p className="relative mt-5 text-[15px] leading-relaxed text-white/60">
            Tempel alamatnya, tekan satu tombol. Crawel membaca susunan
            halamannya, menemukan daftar yang berulang, lalu menamai kolomnya
            sendiri dari isi datanya.
          </p>

          <div className="relative mt-5 space-y-2">
            {[
              ['Nama Produk', 'Logitech MX Master 3S'],
              ['Harga', 'Rp 1.349.000'],
              ['Rating', '4,9'],
            ].map(([nama, contoh]) => (
              <div
                key={nama}
                className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-3.5 py-2.5"
              >
                <Check className="size-3.5 shrink-0 text-signal-400" aria-hidden="true" />
                <span className="text-[13px] font-semibold">{nama}</span>
                <span className="ml-auto truncate font-mono text-[11px] text-white/40">
                  {contoh}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Fitur ─────────────────────────────────────────────────────────────── */

const Fitur = () => (
  <section id="fitur" className="bg-[var(--color-void-950)] py-20 lg:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-6">
      <Eyebrow>Yang Anda dapat</Eyebrow>
      <h2 className="display mt-4 max-w-2xl text-3xl font-extrabold sm:text-4xl lg:text-[2.75rem]">
        Dibangun untuk dipakai, bukan dipelajari
      </h2>

      {/* Bento: kartu utama lebih besar. Kisi seragam membuat semua fitur
          terlihat sama pentingnya - padahal deteksi otomatis yang menjual. */}
      <div className="mt-12 grid gap-4 lg:grid-cols-3">
        <KartuBesar />

        <Kartu
          icon={CalendarClock}
          judul="Jalan sendiri sesuai jadwal"
          isi="Harian, mingguan, atau bulanan. Atur sekali, datanya diperbarui tanpa Anda sentuh lagi."
        />
        <Kartu
          icon={Table2}
          judul="Langsung jadi tabel"
          isi="Angka dikenali sebagai angka, tautan jadi bisa diklik. Unduh CSV atau JSON kapan saja."
        />
        <Kartu
          icon={BellRing}
          judul="Progres terlihat langsung"
          isi="Angka halaman dan baris bergerak saat crawl berjalan. Tidak perlu menebak sudah sampai mana."
        />
        <Kartu
          icon={Database}
          judul="Penyimpanan dengan folder"
          isi="Simpan hasil ke cloud, susun dalam folder, unggah berkas sendiri, bagikan ke publik."
        />
        <Kartu
          icon={ShieldCheck}
          judul="Crawling yang tahu batas"
          isi="robots.txt diikuti, ada jeda antar permintaan, dan batas halaman per job. Situs sumber tidak terbebani."
        />
        <Kartu
          icon={Gauge}
          judul="API untuk integrasi"
          isi="Tarik data crawl langsung ke sistem Anda lewat REST API dengan kunci sendiri."
        />
      </div>
    </div>
  </section>
);

const KartuBesar = () => (
  <div className="group relative overflow-hidden rounded-2xl border border-signal-400/20 bg-gradient-to-br from-signal-400/[0.10] to-transparent p-7 lg:col-span-3">
    <div
      className="pointer-events-none absolute -top-32 -right-16 size-80 rounded-full bg-[radial-gradient(circle,var(--color-signal-500),transparent_70%)] opacity-[0.14] blur-3xl"
      aria-hidden="true"
    />

    <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:gap-12">
      <div className="lg:max-w-md">
        <span className="grid size-11 place-items-center rounded-xl bg-signal-400 text-void-950">
          <Sparkles className="size-5" aria-hidden="true" />
        </span>
        <h3 className="mt-5 text-xl font-extrabold tracking-tight lg:text-2xl">
          Deteksi otomatis yang benar-benar bekerja
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-white/55">
          Daftar di web selalu dibangun dari template - dua puluh kartu produk
          berasal dari satu potongan HTML yang diulang. Crawel memanfaatkan itu:
          ia mencari struktur yang berulang, lalu menamai kolomnya dari isinya.
        </p>
        <Link
          to="/daftar"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-signal-400 hover:underline"
        >
          Coba pada situs Anda
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>

      <div className="grid flex-1 gap-3 sm:grid-cols-3">
        {[
          ['1', 'Tempel URL', 'Alamat apa pun yang bisa diakses publik'],
          ['2', 'Mesin memindai', 'Mencari daftar berulang di struktur halaman'],
          ['3', 'Kolom terisi', 'Nama dan tipe data ditebak dari isinya'],
        ].map(([no, judul, isi]) => (
          <div
            key={no}
            className="rounded-xl border border-white/10 bg-[var(--color-void-1000)]/60 p-4"
          >
            <span className="text-xs font-extrabold text-signal-400">{no}</span>
            <p className="mt-2 text-sm font-bold">{judul}</p>
            <p className="mt-1 text-[12px] leading-relaxed text-white/40">{isi}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Kartu = ({ icon: Icon, judul, isi }) => (
  <div className="group rounded-2xl border border-white/8 bg-white/[0.02] p-6 transition-colors hover:border-white/16 hover:bg-white/[0.04]">
    <span className="grid size-10 place-items-center rounded-xl bg-white/6 text-white/70 transition-colors group-hover:bg-signal-400/15 group-hover:text-signal-400">
      <Icon className="size-[18px]" aria-hidden="true" />
    </span>
    <h3 className="mt-4 text-[15px] font-bold">{judul}</h3>
    <p className="mt-2 text-[13.5px] leading-relaxed text-white/45">{isi}</p>
  </div>
);

/* ── Cara kerja ────────────────────────────────────────────────────────── */

const LANGKAH = [
  {
    no: '01',
    judul: 'Tempel alamatnya',
    isi: 'Salin URL halaman yang datanya ingin Anda ambil - katalog produk, daftar lowongan, arsip berita.',
  },
  {
    no: '02',
    judul: 'Periksa hasil deteksi',
    isi: 'Crawel menampilkan kolom yang ditemukan beserta contoh nilainya. Ubah nama yang kurang pas.',
  },
  {
    no: '03',
    judul: 'Jalankan dan unduh',
    isi: 'Jalankan sekali, atau jadwalkan berulang. Hasilnya masuk tabel yang bisa diunduh dan dibagikan.',
  },
];

const CaraKerja = () => (
  <section
    id="cara-kerja"
    className="relative overflow-hidden border-y border-white/8 bg-[var(--color-void-1000)] py-20 lg:py-28"
  >
    <div className="pointer-events-none absolute inset-0 grid-bg opacity-60" aria-hidden="true" />

    <div className="relative mx-auto max-w-6xl px-5 sm:px-6">
      <Eyebrow>Cara kerja</Eyebrow>
      <h2 className="display mt-4 max-w-2xl text-3xl font-extrabold sm:text-4xl lg:text-[2.75rem]">
        Tiga langkah, dua menit
      </h2>

      <ol className="mt-14 grid gap-10 md:grid-cols-3 md:gap-6">
        {LANGKAH.map((langkah, i) => (
          <li key={langkah.no} className="relative">
            {/* Garis penghubung antar langkah, hanya di layar lebar */}
            {i < LANGKAH.length - 1 && (
              <span
                className="absolute top-5 left-[4.5rem] hidden h-px w-[calc(100%-4rem)] bg-gradient-to-r from-white/15 to-transparent md:block"
                aria-hidden="true"
              />
            )}

            <div className="flex items-center gap-4">
              <span className="grid size-11 shrink-0 place-items-center rounded-xl border border-signal-400/25 bg-signal-400/8 text-sm font-extrabold text-signal-400">
                {langkah.no}
              </span>
            </div>

            <h3 className="mt-5 text-lg font-bold">{langkah.judul}</h3>
            <p className="mt-2.5 text-[14.5px] leading-relaxed text-white/45">{langkah.isi}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

/* ── Harga ─────────────────────────────────────────────────────────────── */

/**
 * Harga cadangan, dipakai hanya selama jawaban server belum tiba.
 *
 * Harus selalu sama dengan `PLANS` di server. Angka lama sepersekian detik
 * masih lebih baik daripada kartu kosong yang melompat, tetapi angka SALAH
 * sepersekian detik adalah janji harga yang tidak bisa ditepati, dan itu
 * dibaca calon pembeli sebagai harga sungguhan.
 */
const PAKET = [
  { nama: 'Gratis', harga: '0', periode: 'bulan', baris: '150', fitur: ['1 crawl bersamaan', '3 job tersimpan'] },
  { nama: 'Starter', harga: '15rb', periode: 'bulan', baris: '3.000', fitur: ['2 crawl bersamaan', '10 job tersimpan'] },
  { nama: 'Growth', harga: '25rb', periode: 'bulan', baris: '10.000', fitur: ['Penjadwalan otomatis', '25 job tersimpan'], unggulan: true },
  { nama: 'Pro', harga: '50rb', periode: 'bulan', baris: '50.000', fitur: ['Jadwal per jam', 'Akses API'] },
  { nama: 'Business', harga: '100rb', periode: 'bulan', baris: '150.000', fitur: ['10 crawl bersamaan', '500 job tersimpan'] },
];

const Harga = () => {
  const plans = usePublicPlans();

  // Selama harga dari server belum tiba, kartu memakai angka statis - lebih
  // baik angka lama sepersekian detik daripada kartu kosong yang melompat.
  const paket = plans
    ? plans.map((p) => ({
        nama: p.name,
        harga: hargaRingkas(p.priceIdr),
        periode: PERIODE_PER[p.periodDays] ?? `${p.periodDays} hari`,
        baris: p.maxRecords.toLocaleString('id-ID'),
        fitur: [
          `${p.maxConcurrentRuns} crawl bersamaan`,
          `${p.maxJobs} job tersimpan`,
          ...(p.scheduleAllowed ? ['Penjadwalan otomatis'] : []),
          ...(p.apiAccess ? ['Akses API'] : []),
        ].slice(0, 3),
        unggulan: p.tagline ? true : p.id === 'growth',
        tagline: p.tagline,
      }))
    : PAKET;

  return (
  <section id="harga" className="bg-[var(--color-void-950)] py-20 lg:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-6">
      <Eyebrow>Harga</Eyebrow>
      <h2 className="display mt-4 max-w-2xl text-3xl font-extrabold sm:text-4xl lg:text-[2.75rem]">
        Bayar sesuai yang dipakai
      </h2>
      <p className="mt-5 max-w-lg text-[17px] leading-relaxed text-white/50">
        Semua paket sudah termasuk deteksi kolom otomatis, ekspor data, dan
        pemberitahuan hasil. Mulai dari yang gratis, naik kalau memang sudah
        terasa kurang. Tidak ada perpanjangan otomatis.
      </p>

      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {paket.map((paket) => (
          <div
            key={paket.nama}
            className={cn(
              'relative flex flex-col rounded-2xl border p-6 transition-colors',
              paket.unggulan
                ? 'border-signal-400/40 bg-signal-400/[0.06]'
                : 'border-white/8 bg-white/[0.02] hover:border-white/16',
            )}
          >
            {paket.unggulan && (
              <span className="absolute -top-2.5 left-6 rounded-full bg-signal-400 px-2.5 py-0.5 text-[10px] font-extrabold text-void-950 uppercase">
                {paket.tagline || 'Terpopuler'}
              </span>
            )}

            <p className="text-sm font-bold text-white/70">{paket.nama}</p>

            <p className="mt-3 flex items-baseline gap-1">
              <span className="text-2xl font-extrabold tracking-tight">
                {paket.harga === '0' ? 'Gratis' : `Rp${paket.harga}`}
              </span>
            </p>
            <p className="mt-0.5 text-xs text-white/35">per {paket.periode}</p>

            <p className="mt-5 border-t border-white/8 pt-5 text-sm">
              <span className="font-extrabold text-signal-400">{paket.baris}</span>
              <span className="text-white/50"> baris data</span>
            </p>

            <ul className="mt-3 flex-1 space-y-1.5">
              {paket.fitur.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[12.5px] text-white/45">
                  <Check className="mt-0.5 size-3 shrink-0 text-signal-400" aria-hidden="true" />
                  {f}
                </li>
              ))}
            </ul>

            <Link
              to="/daftar"
              className={cn(
                'mt-6 rounded-lg py-2.5 text-center text-[13px] font-bold transition-colors',
                paket.unggulan
                  ? 'bg-signal-400 text-void-950 hover:bg-signal-300'
                  : 'border border-white/15 text-white hover:bg-white/5',
              )}
            >
              {paket.harga === '0' ? 'Mulai Gratis' : 'Pilih Paket'}
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);
};

/* ── Kepercayaan ───────────────────────────────────────────────────────── */

/**
 * Pertanyaan yang benar-benar ditanyakan calon pembeli.
 *
 * Jawaban "data Anda disimpan di server yang Anda kelola" sempat ada di sini
 * dan dibuang: itu tidak benar untuk layanan seperti ini, dan calon pembeli
 * yang menemukan sendiri bahwa jawabannya keliru akan meragukan semua jawaban
 * lain di halaman ini.
 */
const TANYA = [
  [
    'Apakah scraping ini legal?',
    'Crawel hanya mengambil halaman yang bisa dibuka siapa saja tanpa masuk akun, dan mengikuti aturan robots.txt situs sumber. Konten di balik login dan data pribadi tidak termasuk yang kami layani. Soal bagaimana datanya Anda pakai setelah terkumpul, itu tetap tanggung jawab Anda.',
  ],
  [
    'Apakah situs yang saya ambil datanya akan terganggu?',
    'Tidak. Setiap crawl memberi jeda antar permintaan dan berhenti pada batas halaman yang Anda tentukan. Kalau situsnya menyebut Crawl-delay di robots.txt, jeda itu yang kami ikuti, bukan jeda kami sendiri.',
  ],
  [
    'Bagaimana kalau halamannya baru muncul setelah JavaScript jalan?',
    'Crawel mencoba cara cepat dulu karena itu jauh lebih hemat. Kalau hasilnya kosong, ia berpindah sendiri ke browser sungguhan yang menjalankan JavaScript-nya. Anda tidak perlu memilih.',
  ],
  [
    'Data hasil crawl saya disimpan di mana?',
    'Di server Crawel, dan hanya bisa dibuka oleh Anda serta anggota ruang kerja yang Anda undang. Berapa lama disimpan mengikuti paket Anda, dan yang lewat masa itu terhapus sendiri. Bisa juga diunduh kapan saja sebagai CSV, JSON, atau Excel.',
  ],
  [
    'Bagaimana cara bayar dan memperpanjangnya?',
    'Lewat QRIS, transfer bank, atau dompet digital. Paket berlaku 30 hari dan tidak diperpanjang otomatis, jadi tidak ada tagihan yang muncul tanpa Anda minta. Kalau diperpanjang sebelum habis, sisa harinya ditambahkan, bukan hangus.',
  ],
  [
    'Kalau kuota saya habis di tengah bulan?',
    'Anda bisa menambah kuota lewat add-on tanpa harus naik paket. Harganya dihitung menurut sisa hari periode berjalan, jadi Anda tidak membayar untuk hari yang sudah lewat.',
  ],
];

const Kepercayaan = ({ faq }) => (
  <section className="border-y border-white/8 bg-[var(--color-void-1000)] py-20 lg:py-28">
    <div className="mx-auto grid max-w-6xl gap-14 px-5 sm:px-6 lg:grid-cols-[0.85fr_1fr] lg:gap-20">
      <div>
        <span className="grid size-11 place-items-center rounded-xl bg-signal-400/12 text-signal-400">
          <ShieldCheck className="size-5" aria-hidden="true" />
        </span>
        <h2 className="display mt-5 text-3xl font-extrabold sm:text-4xl">
          Crawling yang bertanggung jawab
        </h2>
        <p className="mt-5 text-[15px] leading-relaxed text-white/50">
          Alat seperti ini gampang dipakai untuk hal yang tidak semestinya.
          Jadi batasannya kami tulis terbuka di sini, bukan diselipkan di
          halaman syarat dan ketentuan yang tidak pernah dibaca siapa pun.
        </p>
      </div>

      <div className="divide-y divide-white/8 border-y border-white/8">
        {TANYA.map(([tanya, jawab]) => (
          <Akordeon key={tanya} tanya={tanya} jawab={jawab} />
        ))}
      </div>
    </div>
  </section>
);

const Akordeon = ({ tanya, jawab }) => {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-[15px] font-bold">{tanya}</span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-white/40 transition-transform duration-200',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>
      {open && (
        <p className="animate-rise pb-5 text-[14px] leading-relaxed text-white/50">{jawab}</p>
      )}
    </div>
  );
};

/* ── Penutup ───────────────────────────────────────────────────────────── */

const Penutup = () => (
  <section className="relative overflow-hidden py-24 lg:py-32">
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 h-[420px] bg-[radial-gradient(ellipse_60%_100%_at_50%_100%,var(--color-signal-500),transparent_70%)] opacity-[0.13] blur-2xl"
      aria-hidden="true"
    />

    <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-6">
      <h2 className="display text-4xl font-extrabold sm:text-5xl lg:text-6xl">
        Coba pada satu URL.
        <br />
        <span className="text-signal-400">Lihat sendiri hasilnya.</span>
      </h2>
      <p className="mx-auto mt-6 max-w-md text-[17px] leading-relaxed text-white/50">
        Tidak perlu kartu kredit, tidak perlu memasang apa pun. Cukup buka
        halamannya, tempel satu alamat, dan lihat sendiri apa yang keluar.
      </p>

      <Link
        to="/daftar"
        className="group mt-9 inline-flex items-center gap-2 rounded-xl bg-signal-400 px-8 py-4 text-base font-bold text-void-950 transition-transform hover:scale-[1.03] active:scale-100"
      >
        Mulai Gratis
        <ArrowRight
          className="size-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </Link>
    </div>
  </section>
);

/* ── Footer ────────────────────────────────────────────────────────────── */

/* ── Tentang Kami ──────────────────────────────────────────────────────── */

/**
 * Bagian Tentang Kami.
 *
 * Pengunjung yang belum kenal Crawel bertanya "ini siapa" sebelum bertanya
 * "ini bisa apa". Halaman yang langsung menjual tanpa menjawab yang pertama
 * terbaca seperti iklan, dan yang non-teknis paling cepat pergi karenanya.
 *
 * Seluruh isinya dari Konten Landing. Tidak ditampilkan kalau admin belum
 * mengisinya: bagian kosong berjudul "Tentang Kami" lebih buruk daripada tidak
 * ada bagiannya sama sekali.
 */
const Tentang = ({ isi }) => {
  if (!isi?.aktif || (!isi.isi && !isi.poin?.length)) return null;

  const paragraf = String(isi.isi ?? '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <section id="tentang" className="border-t border-white/8 py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <Eyebrow>Tentang Kami</Eyebrow>
        <h2 className="display mt-4 max-w-2xl text-3xl font-extrabold sm:text-4xl">
          {isi.judul || 'Siapa di balik Crawel'}
        </h2>

        <div className="mt-8 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div className="space-y-4">
            {paragraf.map((p, i) => (
              <p key={i} className="text-[15px] leading-relaxed text-white/60">
                {p}
              </p>
            ))}
          </div>

          {isi.poin?.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {isi.poin.map((p, i) => (
                <div key={i} className="rounded-xl border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-sm font-bold text-white">{p.judul}</p>
                  {p.isi && <p className="mt-1 text-sm leading-relaxed text-white/50">{p.isi}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ── Hubungi Kami ──────────────────────────────────────────────────────── */

/**
 * Bagian Hubungi Kami.
 *
 * Tiap cara menghubungi ditampilkan sebagai tautan yang benar-benar bisa
 * ditekan: email membuka aplikasi surat, nomor telepon memanggil, WhatsApp
 * membuka percakapan. Nomor yang cuma tertulis sebagai teks memaksa orang
 * menyalinnya di ponsel, dan sebagian besar tidak jadi menghubungi.
 */
const Kontak = ({ isi }) => {
  if (!isi?.aktif) return null;

  const cara = [
    isi.email && {
      icon: Mail,
      label: 'Email',
      nilai: isi.email,
      href: `mailto:${isi.email}`,
    },
    isi.whatsapp && {
      icon: MessageCircle,
      label: 'WhatsApp',
      nilai: isi.whatsapp,
      // Hanya angkanya yang dipakai; spasi dan tanda hubung membuat tautannya
      // gagal dibuka di sebagian ponsel.
      href: `https://wa.me/${String(isi.whatsapp).replace(/\D/g, '')}`,
    },
    isi.telepon && {
      icon: Phone,
      label: 'Telepon',
      nilai: isi.telepon,
      href: `tel:${String(isi.telepon).replace(/[^\d+]/g, '')}`,
    },
    isi.alamat && { icon: MapPin, label: 'Alamat', nilai: isi.alamat, href: null },
  ].filter(Boolean);

  if (cara.length === 0) return null;

  return (
    <section id="kontak" className="border-t border-white/8 py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        <Eyebrow>Hubungi Kami</Eyebrow>
        <h2 className="display mt-4 max-w-2xl text-3xl font-extrabold sm:text-4xl">
          {isi.judul || 'Ada yang ingin ditanyakan?'}
        </h2>
        {isi.pengantar && (
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-white/60">
            {isi.pengantar}
          </p>
        )}

        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {cara.map((c) => {
            const Isi = (
              <>
                <c.icon className="size-5 text-signal-400" aria-hidden="true" />
                <p className="mt-3 text-xs font-bold tracking-wide text-white/40 uppercase">
                  {c.label}
                </p>
                <p className="mt-1 text-sm leading-snug font-semibold break-words text-white">
                  {c.nilai}
                </p>
              </>
            );

            return c.href ? (
              <a
                key={c.label}
                href={c.href}
                className="rounded-xl border border-white/8 bg-white/[0.03] p-4 transition-colors hover:border-signal-400/40 hover:bg-white/[0.06]"
              >
                {Isi}
              </a>
            ) : (
              <div key={c.label} className="rounded-xl border border-white/8 bg-white/[0.03] p-4">
                {Isi}
              </div>
            );
          })}
        </div>

        {isi.jamOperasional && (
          <p className="mt-5 flex items-center gap-2 text-sm text-white/40">
            <Clock className="size-4" aria-hidden="true" />
            {isi.jamOperasional}
          </p>
        )}
      </div>
    </section>
  );
};

/* ── Footer ────────────────────────────────────────────────────────────── */

const SOSIAL = [
  ['instagram', 'Instagram'],
  ['linkedin', 'LinkedIn'],
  ['twitter', 'X'],
  ['youtube', 'YouTube'],
  ['github', 'GitHub'],
];

/**
 * Footer.
 *
 * Empat kolom, dan yang paling kanan sengaja diisi kontak lengkap: alamat
 * usaha dan nomor telepon yang bisa dihubungi adalah tanda paling sederhana
 * bahwa di balik situs ini ada usaha yang nyata. Sebagian penyedia pembayaran
 * juga menuntutnya tercantum.
 *
 * Yang kosong tidak ditampilkan. Kolom berisi label tanpa nilai lebih buruk
 * daripada kolom yang tidak ada.
 */
export const Footer = ({ kontak, konten }) => {
  const k = konten?.kontak ?? {};
  const sosial = konten?.sosial ?? {};
  const email = k.email || kontak;
  const nama = k.namaUsaha || 'Crawel';
  const tautanSosial = SOSIAL.filter(([kunci]) => sosial[kunci]);

  return (
    <footer className="border-t border-white/8 bg-[var(--color-void-1000)]">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:px-6">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Logo variant="light" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/40">
              Kumpulkan data dari web secara terjadwal, tanpa menulis satu baris kode.
            </p>
            {tautanSosial.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-3">
                {tautanSosial.map(([kunci, label]) => (
                  <a
                    key={kunci}
                    href={sosial[kunci]}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm text-white/40 underline-offset-4 transition-colors hover:text-white hover:underline"
                  >
                    {label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-bold tracking-[0.14em] text-white/70 uppercase">Produk</p>
            <nav className="mt-4 flex flex-col gap-2.5" aria-label="Tautan produk">
              {TAUTAN.map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  className="text-sm text-white/40 transition-colors hover:text-white"
                >
                  {label}
                </a>
              ))}
            </nav>
          </div>

          <div>
            <p className="text-xs font-bold tracking-[0.14em] text-white/70 uppercase">Crawel</p>
            <nav className="mt-4 flex flex-col gap-2.5" aria-label="Tautan halaman">
              <Link to="/fitur" className="text-sm text-white/40 transition-colors hover:text-white">
                Fitur
              </Link>
              <Link to="/harga" className="text-sm text-white/40 transition-colors hover:text-white">
                Harga
              </Link>
              <Link
                to="/dokumentasi"
                className="text-sm text-white/40 transition-colors hover:text-white"
              >
                Dokumentasi
              </Link>
              <a href="#tentang" className="text-sm text-white/40 transition-colors hover:text-white">
                Tentang Kami
              </a>
              <Link
                to="/hubungi-kami"
                className="text-sm text-white/40 transition-colors hover:text-white"
              >
                Hubungi Kami
              </Link>
              <Link to="/masuk" className="text-sm text-white/40 transition-colors hover:text-white">
                Masuk
              </Link>
            </nav>
          </div>

          <div>
            <p className="text-xs font-bold tracking-[0.14em] text-white/70 uppercase">Kontak</p>
            <div className="mt-4 flex flex-col gap-3">
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex items-start gap-2.5 text-sm text-white/40 transition-colors hover:text-white"
                >
                  <Mail className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span className="break-all">{email}</span>
                </a>
              )}
              {k.telepon && (
                <a
                  href={`tel:${String(k.telepon).replace(/[^\d+]/g, '')}`}
                  className="flex items-start gap-2.5 text-sm text-white/40 transition-colors hover:text-white"
                >
                  <Phone className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>{k.telepon}</span>
                </a>
              )}
              {k.whatsapp && (
                <a
                  href={`https://wa.me/${String(k.whatsapp).replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-start gap-2.5 text-sm text-white/40 transition-colors hover:text-white"
                >
                  <MessageCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>WhatsApp {k.whatsapp}</span>
                </a>
              )}
              {k.alamat && (
                <p className="flex items-start gap-2.5 text-sm leading-relaxed text-white/40">
                  <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>{k.alamat}</span>
                </p>
              )}
              {k.jamOperasional && (
                <p className="flex items-start gap-2.5 text-sm text-white/40">
                  <Clock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>{k.jamOperasional}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-white/8 pt-6 sm:flex-row sm:items-center">
          <p className="text-sm text-white/25">
            © {new Date().getFullYear()} {nama}. Seluruh hak dilindungi.
          </p>
          <p className="text-sm text-white/25">
            Dibuat di Indonesia untuk pekerjaan data yang berulang.
          </p>
        </div>
      </div>
    </footer>
  );
};

/* ── Bersama ───────────────────────────────────────────────────────────── */

const Eyebrow = ({ children, tone = 'light' }) => (
  <p
    className={cn(
      'flex items-center gap-2.5 text-xs font-bold tracking-[0.14em] uppercase',
      tone === 'dark' ? 'text-ink-500' : 'text-signal-400',
    )}
  >
    <span
      className={cn('h-px w-7', tone === 'dark' ? 'bg-ink-300' : 'bg-signal-400/40')}
      aria-hidden="true"
    />
    {children}
  </p>
);
